import { Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  BetaIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { SettingButton } from 'components/common/settings-dialog/button';
import { SettingForm } from 'components/common/settings-dialog/form';
import { InactiveCallout } from 'components/common/settings-dialog/inactive-callout';
import { SettingRow } from 'components/common/settings-dialog/row';
import { VisionLowerMachineConfirmation } from 'components/machine/dialogs/vision-calibration/lowered-confirmation';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { format } from 'date-fns';
import {
  isTrackingDeviceBeta,
  isTrackingDeviceEnabled,
  isTrackingDeviceVisible,
} from 'enums/beta';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { CalibrateProc } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  FF_NAME_ON_DEMAND_MODE,
  TrackingDevice,
} from 'lib_ts/enums/training.enums';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IVisionCameraCalibration } from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { useContext, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { VisionCameraCalibrationsService } from 'services/vision-camera-calibrations';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'MachineTrackingDeviceTab';

interface IProps {
  onSave: (payload: Partial<IMachine>) => void;
}

export const MachineTrackingDeviceTab = (props: IProps) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);

  const current = useAuthStore((s) => s.current);
  const isInGame = useAuthStore((s) => s.isInGame);
  const { machine, isActive, calibrate, openDialog } =
    useContext(MachineContext);

  const [value, setValue] = useState<IMachine>({ ...machine });

  const [dialogLowerMachine, setDialogLowerMachine] = useState<{
    onConfirm: () => void;
    key: number;
  }>();

  const [lastCompletedVcc, setLastCompletedVcc] =
    useState<IVisionCameraCalibration>();

  useEffect(() => {
    const getLastCompleted = async () => {
      // the last completed VCC may not be from this install session - find the last completed one for info purposes
      const response =
        await VisionCameraCalibrationsService.getInstance().getLastCompleted(
          machine.machineID
        );

      setLastCompletedVcc(response);
    };

    getLastCompleted();
  }, [machine.machineID]);

  useEffect(() => {
    setValue({ ...machine });
  }, [machine]);

  const deviceOptions = useMemo(
    () =>
      Object.values(TrackingDevice).map((m) => {
        const o: IOptionExt = {
          label: m,
          value: m,
          invisible: !isTrackingDeviceVisible(m, machine),
          disabled: !isTrackingDeviceEnabled(m, machine),
          suffix: isTrackingDeviceBeta(m) ? <BetaIcon /> : undefined,
        };

        return o;
      }),
    [machine]
  );

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <SettingForm>
        {!isActive && <InactiveCallout />}

        <SettingRow
          header="settings.tracking-device"
          description={<Text>{t('settings.tracking-device-msg')}</Text>}
          input={
            <CommonSelectInput
              id="machine-tracking-device"
              options={deviceOptions}
              disabled={!isActive || isInGame}
              value={value.tracking_device}
              onChange={(v) => {
                if (v !== TrackingDevice.TrajektVision && onDemand) {
                  // do nothing
                  NotifyHelper.warning({
                    message_md:
                      'Please contact support to disable On-Demand before switching away from Trajekt Vision.',
                  });
                  return;
                }

                const safeValue = v as TrackingDevice;

                setValue({
                  ...value,
                  tracking_device: safeValue,
                });

                props.onSave({
                  _id: value._id,
                  tracking_device: safeValue,
                });
              }}
            />
          }
        />

        {value.tracking_device === TrackingDevice.TrackmanB1 && (
          <SettingRow
            separatorBefore
            header="settings.trackman-first-name-string"
            description={
              <Text>
                This string must be entered in the player First Name field in
                the Trackman iPad App.
              </Text>
            }
            input={
              <CommonTextInput
                id="machine-trackman-first-name"
                value={machine.machineID}
                monospace
                disabled
              />
            }
          />
        )}

        {value.tracking_device === TrackingDevice.TrackmanB1 && (
          <SettingRow
            separatorBefore
            header="settings.trackman-last-name-string"
            description={
              <Text>
                This string must be entered in the player Last Name field in the
                Trackman iPad App.
              </Text>
            }
            input={
              <CommonTextInput
                id="machine-trackman-last-name"
                value={machine._id.slice(-4)}
                monospace
                disabled
              />
            }
          />
        )}

        {value.tracking_device === TrackingDevice.TrackmanB1 && (
          <SettingRow
            separatorBefore
            header={`Trackman ${t('settings.calibration')}`}
            description={
              <Text>
                Display a mound outline on the Arc using the projector.
              </Text>
            }
            input={
              <SettingButton
                label="settings.calibrate"
                disabled={!isActive}
                onClick={() => {
                  calibrate([CalibrateProc.ProjectorMound], COMPONENT_NAME);
                }}
              />
            }
          />
        )}

        {value.tracking_device === TrackingDevice.RapsodoV3PRO && (
          <SettingRow
            separatorBefore
            header="settings.rapsodo-serial"
            description={
              <ReactMarkdown
                children={t(
                  machine.sandbox
                    ? 'settings.rapsodo-serial-sandbox-msg'
                    : 'settings.rapsodo-serial-msg'
                )}
              />
            }
            input={
              <CommonTextInput
                id="machine-rapsodo-serial"
                value={value.rapsodo_serial}
                disabled={!isActive || value.sandbox || isInGame}
                onChange={(v) => {
                  const safeValue = v ?? '';

                  setValue({
                    ...value,
                    rapsodo_serial: safeValue,
                  });

                  props.onSave({
                    _id: value._id,
                    rapsodo_serial: safeValue.trim(),
                  });
                }}
                debounceMs={1_000}
                monospace
              />
            }
          />
        )}

        {current.role === UserRole.admin && machine.enable_trajekt_vision && (
          <SettingRow
            separatorBefore
            header="settings.trajekt-vision-stereo-camera-calibration"
            description={
              <Flex direction="column" gap={RADIX.FLEX.GAP.XS}>
                <Text>{t('settings.trajekt-vision-camera-msg')}</Text>
                {lastCompletedVcc && (
                  <Text size="1" style={{ color: 'var(--gray-10)' }}>
                    {t('common.last-completed-on-x', {
                      x: format(
                        new Date(lastCompletedVcc._changed),
                        'EEE. MMM dd, yyyy h:mm a'
                      ),
                    })}
                  </Text>
                )}
              </Flex>
            }
            headerDecoration={<SuperAdminIcon />}
            input={
              <SettingButton
                label="settings.calibrate"
                disabled={!isActive}
                onClick={() =>
                  setDialogLowerMachine({
                    key: Date.now(),
                    onConfirm: () => openDialog(MachineDialogMode.VisionCamera),
                  })
                }
              />
            }
          />
        )}

        {machine.enable_trajekt_vision && (
          <SettingRow
            separatorBefore
            header="settings.trajekt-vision-distance-to-plate-calibration"
            description={
              <Flex direction="column" gap={RADIX.FLEX.GAP.XS}>
                <Text>{t('settings.trajekt-vision-distance-msg')}</Text>
                {machine.last_vision_plate_calibration_changed && (
                  <Text size="1" style={{ color: 'var(--gray-10)' }}>
                    {t('common.last-completed-on-x', {
                      x: format(
                        new Date(machine.last_vision_plate_calibration_changed),
                        'EEE. MMM dd, yyyy h:mm a'
                      ),
                    })}
                  </Text>
                )}
              </Flex>
            }
            input={
              <SettingButton
                label="settings.calibrate"
                disabled={!isActive}
                onClick={() =>
                  setDialogLowerMachine({
                    key: Date.now(),
                    onConfirm: () =>
                      openDialog(MachineDialogMode.VisionDistance),
                  })
                }
              />
            }
          />
        )}

        {dialogLowerMachine && (
          <VisionLowerMachineConfirmation
            key={dialogLowerMachine.key}
            onConfirm={() => dialogLowerMachine.onConfirm()}
            onClose={() => setDialogLowerMachine(undefined)}
            allowLowering
          />
        )}
      </SettingForm>
    </ErrorBoundary>
  );
};
